import { useState, useRef, useEffect } from 'react'
import { X, Minimize2, Maximize2, Move, Volume2, VolumeX } from 'lucide-react'
import EmbeddedCameraStream from './EmbeddedCameraStream'

const PictureInPicture = ({ 
    camera, 
    isVisible, 
    onClose, 
    initialPosition = { x: 20, y: 20 },
    initialSize = { width: 320, height: 240 }
}) => {
    const [position, setPosition] = useState(initialPosition)
    const [size, setSize] = useState(initialSize)
    const [isDragging, setIsDragging] = useState(false)
    const [isResizing, setIsResizing] = useState(false)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const [isMinimized, setIsMinimized] = useState(false)
    const [isMuted, setIsMuted] = useState(true)
    
    const pipRef = useRef(null)
    const resizeHandleRef = useRef(null)

    // Handle dragging
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const newX = e.clientX - dragOffset.x
                const newY = e.clientY - dragOffset.y

                // Keep within viewport bounds
                const maxX = window.innerWidth - size.width
                const maxY = window.innerHeight - (isMinimized ? 60 : size.height)

                setPosition({
                    x: Math.max(0, Math.min(newX, maxX)),
                    y: Math.max(0, Math.min(newY, maxY))
                })
            }
        }

        const handleMouseUp = () => {
            setIsDragging(false)
            setIsResizing(false)
        }

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.body.style.userSelect = 'none'
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.body.style.userSelect = ''
        }
    }, [isDragging, isResizing, dragOffset, size.width, size.height, isMinimized])

    // Handle resizing
    useEffect(() => {
        const handleResizeMove = (e) => {
            if (isResizing) {
                const rect = pipRef.current?.getBoundingClientRect()
                if (rect) {
                    const newWidth = Math.max(240, e.clientX - rect.left)
                    const newHeight = Math.max(180, e.clientY - rect.top)
                    
                    // Maintain aspect ratio (16:9)
                    const aspectRatio = 16 / 9
                    const calculatedHeight = newWidth / aspectRatio
                    
                    setSize({
                        width: Math.min(newWidth, window.innerWidth - position.x),
                        height: Math.min(calculatedHeight, window.innerHeight - position.y)
                    })
                }
            }
        }

        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMove)
        }

        return () => {
            document.removeEventListener('mousemove', handleResizeMove)
        }
    }, [isResizing, position])

    // Constrain position when window resizes
    useEffect(() => {
        const handleWindowResize = () => {
            const maxX = window.innerWidth - size.width
            const maxY = window.innerHeight - (isMinimized ? 60 : size.height)

            setPosition(prev => ({
                x: Math.max(0, Math.min(prev.x, maxX)),
                y: Math.max(0, Math.min(prev.y, maxY))
            }))
        }

        window.addEventListener('resize', handleWindowResize)
        return () => window.removeEventListener('resize', handleWindowResize)
    }, [size, isMinimized])

    const handleDragStart = (e) => {
        setIsDragging(true)
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        })
    }

    const handleResizeStart = (e) => {
        e.stopPropagation()
        setIsResizing(true)
    }

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized)
    }

    const toggleMute = () => {
        setIsMuted(!isMuted)
    }

    if (!isVisible || !camera) return null

    return (
        <div
            ref={pipRef}
            className={`fixed z-50 bg-black border-2 border-gray-800 shadow-2xl overflow-hidden transition-all duration-200 ${
                isDragging ? 'cursor-grabbing' : 'cursor-default'
            }`}
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                height: isMinimized ? '60px' : `${size.height}px`,
                borderRadius: '8px'
            }}
        >
            {/* Header */}
            <div
                className="bg-gray-900 text-white px-3 py-2 flex items-center justify-between cursor-grab select-none"
                onMouseDown={handleDragStart}
            >
                <div className="flex items-center space-x-2 min-w-0">
                    <div className={`w-2 h-2 rounded-full ${camera.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-sm font-medium truncate">
                        {camera.name}
                    </span>
                </div>
                
                <div className="flex items-center space-x-1">
                    <button
                        onClick={toggleMute}
                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                        title={isMuted ? 'Unmute' : 'Mute'}
                    >
                        {isMuted ? (
                            <VolumeX className="h-3 w-3" />
                        ) : (
                            <Volume2 className="h-3 w-3" />
                        )}
                    </button>
                    
                    <button
                        onClick={toggleMinimize}
                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                        title={isMinimized ? 'Restore' : 'Minimize'}
                    >
                        {isMinimized ? (
                            <Maximize2 className="h-3 w-3" />
                        ) : (
                            <Minimize2 className="h-3 w-3" />
                        )}
                    </button>
                    
                    <button
                        onClick={onClose}
                        className="p-1 hover:bg-red-600 rounded transition-colors"
                        title="Close"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </div>
            </div>

            {/* Video Content */}
            {!isMinimized && (
                <div className="relative h-full">
                    <div style={{ height: `${size.height - 42}px` }}>
                        <EmbeddedCameraStream
                            camera={camera}
                            isExpanded={true}
                            onToggleExpand={() => {}}
                            isMinimal={true}
                        />
                    </div>
                    
                    {/* Resize handle */}
                    <div
                        ref={resizeHandleRef}
                        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
                        onMouseDown={handleResizeStart}
                    >
                        <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-white opacity-50" />
                    </div>
                </div>
            )}

            {/* Minimized state drag indicator */}
            {isMinimized && (
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <Move className="h-4 w-4 text-gray-400" />
                </div>
            )}
        </div>
    )
}

export default PictureInPicture